import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Scissors, Crown, Sparkles, Phone, MessageCircle, Calendar, MapPin, Clock, Star } from 'lucide-react';
import FAQ from '@/components/FAQ';

const Home = () => {
  const handleWhatsApp = () => {
    window.open('https://wa.me/33767632259', '_blank');
  };

  const handleCall = () => {
    window.open('tel:+33767632259', '_self');
  };

  const services = [
    {
      icon: <Scissors className="h-8 w-8" />,
      title: "Coiffure Hommes",
      description: "Des coupes tendances et élégantes",
      price: "À partir de 15€",
      link: "/services"
    },
    {
      icon: <Crown className="h-8 w-8" />,
      title: "Tresses Femmes", 
      description: "Des créations modernes et traditionnelles",
      price: "À partir de 40€",
      link: "/services"
    },
    {
      icon: <Sparkles className="h-8 w-8" />,
      title: "Ongles",
      description: "Des prestations raffinées pour des mains parfaites",
      price: "À partir de 25€",
      link: "/services"
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/IMG-20251103-WA0023.jpg"
            alt="Pablo Coiffure Salon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="animate-fade-in-up">
            <img 
              src="/images/pablo-coiffure-logo.png" 
              alt="Pablo Coiffure Logo" 
              className="h-24 w-auto mx-auto mb-8 animate-glow"
            />
            
            <h1 className="heading-xl md:text-6xl lg:text-7xl font-display mb-6">
              <span className="text-gradient">PABLO COIFFURE</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-primary font-bold mb-4 tracking-wide">
              L'art de sublimer hommes et femmes
            </p>
            
            <p className="text-lg md:text-xl text-primary/80 font-medium mb-6">
              À Lyon 7ème • Ouvert 24h/24
            </p>
            
            <p className="text-enhanced mb-8 max-w-3xl mx-auto text-center">
              <strong className="text-white">Bienvenue chez Pablo Coiffure</strong>, le salon de beauté mixte de référence à Lyon. <br/>
              Coiffure, tresses, ongles : nous sublimons votre style avec <span className="text-primary font-semibold">passion, précision et créativité</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button
                onClick={handleCall}
                size="lg"
                className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-xl hover:shadow-primary/30 transform hover:scale-105 transition-all duration-300"
              >
                <Phone className="h-5 w-5 mr-2" />
                Appeler maintenant
              </Button>
              
              <Button
                onClick={handleWhatsApp}
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transform hover:scale-105 transition-all duration-300"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                WhatsApp
              </Button>
              
              <Link to="/booking">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-black transform hover:scale-105 transition-all duration-300"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Prendre rendez-vous
                </Button>
              </Link>
            </div>

            {/* Info rapide */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span>94 Grande Rue de la Guillotière, 69007 Lyon</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                <span>Ouvert 24h/24 - Lundi au Samedi</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Nos <span className="text-gradient">Services</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Des prestations professionnelles, des produits de qualité, et un savoir-faire unique pour chaque type de cheveux.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-luxury group cursor-pointer">
                <CardContent className="p-8 text-center">
                  <div className="text-primary mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {service.description}
                  </p>
                  <p className="text-primary font-semibold text-lg mb-6">
                    {service.price}
                  </p>
                  <Link to={service.link}>
                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                      En savoir plus
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
                L'histoire de <span className="text-gradient">Pablo Coiffure</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Créé par Pablo Domingos, passionné de coiffure depuis son plus jeune âge, 
                Pablo Coiffure est né d'un rêve : offrir à chaque client une expérience élégante, moderne et sur mesure.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Avec un style unique alliant technicité, précision et créativité, Pablo sublime aussi bien les hommes que les femmes, 
                en respectant l'identité et la personnalité de chacun.
              </p>
              <Link to="/about">
                <Button className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg hover:shadow-primary/25">
                  Découvrir notre histoire
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src="/images/pablo-fondateur.jpg"
                alt="Pablo Domingos - Fondateur de Pablo Coiffure"
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-xl font-bold mb-1">Pablo Domingos</h3>
                <p className="text-sm text-primary font-medium">Fondateur & Maître Coiffeur</p>
                <p className="text-xs text-gray-300 mt-1">"Chaque client mérite l'excellence"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="heading-lg font-display mb-6">
              Ce que disent nos <span className="text-gradient">clients</span>
            </h2>
            <p className="text-enhanced max-w-2xl mx-auto">
              La satisfaction de nos clients est notre plus belle récompense. Découvrez leurs témoignages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "Marie L.",
                service: "Tresses box braids",
                text: "Pablo est un véritable artiste ! Mes tresses sont magnifiques, parfaitement alignées et tiennent depuis 2 mois. L'accueil est chaleureux et le salon très propre.",
                rating: 5,
                date: "Il y a 2 semaines"
              },
              {
                name: "Thomas M.",
                service: "Coupe + barbe",
                text: "Coupe impeccable, dégradé parfait et barbe taillée avec précision. Pablo prend le temps d'écouter et de conseiller. Mon coiffeur désormais !",
                rating: 5,
                date: "Il y a 1 semaine"
              },
              {
                name: "Fatou S.",
                service: "Défrisage + soin",
                text: "Enfin un salon qui comprend parfaitement les cheveux crépus ! Pablo maîtrise toutes les techniques et utilise d'excellents produits. Merci Pablo !",
                rating: 5,
                date: "Il y a 3 jours"
              }
            ].map((testimonial, index) => (
              <Card key={index} className="card-luxury hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-primary fill-current" />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">{testimonial.date}</span>
                  </div>
                  
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-3">
                      {testimonial.service}
                    </span>
                  </div>
                  
                  <p className="text-muted-foreground mb-4 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-3">
                      <span className="text-primary font-bold text-sm">{testimonial.name.charAt(0)}</span>
                    </div>
                    <p className="font-semibold text-primary">
                      {testimonial.name}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 to-primary-glow/10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Prêt à transformer votre <span className="text-gradient">style</span> ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Réservez dès maintenant votre rendez-vous et découvrez l'excellence de Pablo Coiffure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-xl hover:shadow-primary/30 transform hover:scale-105 transition-all duration-300"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Réserver maintenant
              </Button>
            </Link>
            <Button
              onClick={handleWhatsApp}
              variant="outline"
              size="lg"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transform hover:scale-105 transition-all duration-300"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />
    </div>
  );
};

export default Home;